import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { useEffect, useState } from 'react'
import api from '../../helpers/apiRequester'
import { InputFieldAddressLookup } from '../reusable/inputFieldAddressLookup'
import { InputFieldText } from '../reusable/inputFieldText'
import styles from './styles.module.scss'
import { useAuthUserData } from '../../authUserProvider'

export const CreateGorillaClient = () => {
    const authUser = useAuthUserData()

    const [addressText, setAddressText] = useState('')
    const [addressOne, setAddressOne] = useState('')
    const [addressTwo, setAddressTwo] = useState('')
    const [addressThree, setAddressThree] = useState('')
    const [addressTown, setAddressTown] = useState('')
    const [addressCounty, setAddressCounty] = useState('')
    const [addressPostcode, setAddressPostcode] = useState('')

    const [clientData, setClientData] = useState({
        firstname: '',
        lastname: '',
        title: '',
        dob: '',
        email: '',
        telephone: '',
        address_line_one: '',
        address_line_two: '',
        address_line_three: '',
        city: '',
        county: '',
        postcode: '',
        origin: '',
        source_agent_name: authUser?.name || '',
    })

    const [errors, setErrors] = useState({
        email: '',
        telephone: '',
        dob: '',
    })

    useEffect(() => {
        setClientData((prevData) => ({
            ...prevData,
            origin: window.location.href,
        }))
    }, [])

    const validateDOB = (dob: string) => {
        const birthDate = new Date(dob)
        const today = new Date()
        let age = today.getFullYear() - birthDate.getFullYear()
        const monthDiff = today.getMonth() - birthDate.getMonth()

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--
        }

        if (age < 18) {
            return 'Client must be at least 18 years old'
        }
        if (age > 100) {
            return 'Client cannot be older than 100 years'
        }
        return ''
    }

    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setClientData((prevData) => ({ ...prevData, [name]: value }))

        if (name === 'dob') {
            const dobError = validateDOB(value)
            setErrors((prevErrors) => ({ ...prevErrors, dob: dobError }))
        }
    }

    const saveValue = ({ field, value }: { field: string; value: string }) => {
        if (field === 'searchAddress') {
            saveAddress(value)
            return { success: true, error: '' }
        }

        switch (field) {
            case 'address_line_one':
                setAddressOne(value)
                break
            case 'address_line_two':
                setAddressTwo(value)
                break
            case 'address_line_three':
                setAddressThree(value)
                break
            case 'city':
                setAddressTown(value)
                break
            case 'county':
                setAddressCounty(value)
                break
            case 'postcode':
                setAddressPostcode(value)
                break
            default:
                break
        }

        setClientData((prevData) => ({
            ...prevData,
            [field]: value,
        }))

        if (field === 'email' && value === '') {
            setErrors((prevErrors) => ({ ...prevErrors, email: 'Invalid email' }))
            return { success: false, error: 'Invalid email' }
        }

        if (field === 'telephone' && value === '') {
            setErrors((prevErrors) => ({
                ...prevErrors,
                telephone: 'Invalid phone number',
            }))
            return { success: false, error: 'Invalid phone number' }
        }

        if (field === 'dob') {
            const dobError = validateDOB(value)
            setErrors((prevErrors) => ({ ...prevErrors, dob: dobError }))
            return { success: dobError === '', error: dobError }
        }

        setErrors((prevErrors) => ({ ...prevErrors, [field]: '' }))
        return { success: true, error: '' }
    }

    const saveAddress = (addressString: string) => {
        setAddressText('')
        setAddressOne('')
        setAddressTwo('')
        setAddressThree('')
        setAddressTown('')
        setAddressCounty('')
        setAddressPostcode('')

        setClientData((prevData) => ({
            ...prevData,
            address_line_one: '',
            address_line_two: '',
            address_line_three: '',
            city: '',
            county: '',
            postcode: '',
        }))

        setAddressText(addressString)
        const addressArr = addressString.split(', ')

        const lineOne = addressArr[0] || ''
        const lineTwo = addressArr.length > 4 ? addressArr[1] || '' : ''
        const lineThree = addressArr.length > 5 ? addressArr[2] || '' : ''
        const town =
            addressArr.length == 3
                ? addressArr[addressArr.length - 2]
                : addressArr.length >= 4
                ? addressArr[addressArr.length - 3] || ''
                : ''
        const county = addressArr.length == 3 ? '' : addressArr[addressArr.length - 2] || ''
        const postcode = addressArr[addressArr.length - 1] || ''

        setAddressOne(lineOne)
        setClientData((prevData) => ({ ...prevData, address_line_one: lineOne }))

        setAddressTwo(lineTwo)
        setClientData((prevData) => ({ ...prevData, address_line_two: lineTwo }))

        setAddressThree(lineThree)
        setClientData((prevData) => ({
            ...prevData,
            address_line_three: lineThree,
        }))

        setAddressTown(town)
        setClientData((prevData) => ({ ...prevData, city: town }))

        setAddressCounty(county)
        setClientData((prevData) => ({ ...prevData, county: county }))

        setAddressPostcode(postcode)
        setClientData((prevData) => ({ ...prevData, postcode: postcode }))
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (!clientData.address_line_one || !clientData.postcode) {
            Report.failure('Error', 'Address is required.', 'Okay')
            return
        }

        if (errors.email || errors.telephone || errors.dob) {
            Report.failure('Error', 'Please fix the validation errors before submitting.', 'Okay')
            return
        }

        Loading.circle('Saving...')

        const submissionData = {
            ...clientData,
            leadSource: clientData.origin,
            raw_address: addressText,
        }

        try {
            const apiResult = await api({
                url: 'create-gorilla-client',
                method: 'POST',
                data: submissionData,
            })

            Loading.remove()
            if (!apiResult.data.success) {
                return Report.failure('Error', apiResult.data.error || 'An error occurred.', 'Okay')
            }

            Report.success('Success', 'Client created successfully.', 'Okay', () => {
                window.location.reload()
            })
        } catch (error: any) {
            Loading.remove()
            Report.failure(
                'Error',
                error.response?.data?.error || 'An error occurred while creating the client.',
                'Okay'
            )
        }
    }

    return (
        <div className={styles.clientView}>
            <h3>
                <span>Client Information</span>
            </h3>
            <form onSubmit={handleSubmit}>
                <div className={styles.nameFieldsContainer}>
                    <fieldset className={styles.titleField}>
                        <label>Title</label>
                        <select
                            className={styles.textInput}
                            name="title"
                            value={clientData.title}
                            onChange={onChange}
                            required
                        >
                            <option value="">Title</option>
                            <option value="Mr">Mr</option>
                            <option value="Mrs">Mrs</option>
                            <option value="Ms">Ms</option>
                            <option value="Miss">Miss</option>
                            <option value="Dr">Dr</option>
                        </select>
                    </fieldset>
                    <fieldset className={styles.nameField}>
                        <label>First Name</label>
                        <input
                            className={styles.textInput}
                            name="firstname"
                            value={clientData.firstname}
                            onChange={onChange}
                            type="text"
                            required
                        />
                    </fieldset>
                    <fieldset className={styles.nameField}>
                        <label>Last Name</label>
                        <input
                            className={styles.textInput}
                            name="lastname"
                            value={clientData.lastname}
                            onChange={onChange}
                            type="text"
                            required
                        />
                    </fieldset>
                </div>

                <fieldset>
                    <label>Date of Birth</label>
                    <InputFieldText
                        fieldName="dob"
                        fieldType="date"
                        saveValue={saveValue}
                        populateValue={clientData.dob}
                        required
                        showErrors
                    />
                </fieldset>

                <fieldset>
                    <label>Email</label>
                    <InputFieldText
                        fieldName="email"
                        fieldType="email"
                        saveValue={saveValue}
                        populateValue={clientData.email}
                        required
                        showErrors
                    />
                </fieldset>

                <fieldset>
                    <label>Telephone</label>
                    <InputFieldText
                        fieldName="telephone"
                        fieldType="tel"
                        saveValue={saveValue}
                        populateValue={clientData.telephone}
                        required
                        showErrors
                    />
                </fieldset>

                <fieldset>
                    <label>Search Address</label>
                    <InputFieldAddressLookup fieldName="searchAddress" saveValue={saveValue} enhanced={true} />
                </fieldset>

                {addressText.length > 0 && (
                    <>
                        <fieldset>
                            <label>Address Line 1</label>
                            <InputFieldText
                                fieldName="address_line_one"
                                saveValue={saveValue}
                                populateValue={addressOne}
                                required
                                showErrors
                            />
                        </fieldset>

                        {addressTwo && (
                            <fieldset>
                                <label>Address Line 2</label>
                                <InputFieldText
                                    fieldName="address_line_two"
                                    saveValue={saveValue}
                                    populateValue={addressTwo}
                                />
                            </fieldset>
                        )}

                        {addressThree && (
                            <fieldset>
                                <label>Address Line 3</label>
                                <InputFieldText
                                    fieldName="address_line_three"
                                    saveValue={saveValue}
                                    populateValue={addressThree}
                                />
                            </fieldset>
                        )}

                        <fieldset>
                            <label>City</label>
                            <InputFieldText
                                fieldName="city"
                                saveValue={saveValue}
                                populateValue={addressTown}
                                required
                                showErrors
                            />
                        </fieldset>
                        <fieldset>
                            <label>County</label>
                            <InputFieldText
                                fieldName="county"
                                saveValue={saveValue}
                                populateValue={addressCounty}
                                showErrors
                            />
                        </fieldset>

                        <fieldset>
                            <label>Postcode</label>
                            <InputFieldText
                                fieldName="postcode"
                                saveValue={saveValue}
                                populateValue={addressPostcode}
                                required
                                showErrors
                            />
                        </fieldset>
                    </>
                )}

                <button type="submit" className={styles.submitButton}>
                    Add New Client
                </button>
            </form>
        </div>
    )
}
